import { QueryCache, MutationCache, QueryClient } from '@tanstack/react-query';
import { logFrontendError } from './errorLog.js';
import { toast } from './toast.jsx';

function friendlyMessage(error: unknown): string {
  if (error instanceof Error && error.name === 'ApiError') {
    const status = (error as unknown as { status?: number }).status;
    if (status === 401) return 'Sessão expirada — faça login novamente.';
    // O backend já manda mensagens específicas e seguras de mostrar em 403
    // — tanto de RBAC ("Permissão necessária: x.y") quanto de regra de
    // negócio (ex: "Aprovador não pode decidir o próprio timesheet"). Usar
    // sempre o genérico aqui escondia o motivo real do usuário.
    if (status === 403) return error.message || 'Você não tem permissão para esta ação.';
    if (status === 404) return 'Registro não encontrado — pode ter sido removido.';
    if (status === 409) return error.message || 'Conflito: o registro já existe.';
    if (status === 422 || status === 400) return error.message || 'Dados inválidos.';
    if (status && status >= 500) return 'Erro no servidor — tente novamente.';
    return error.message || 'Erro ao comunicar com o servidor.';
  }
  if (error instanceof TypeError) return 'Sem conexão com o servidor.';
  return 'Erro inesperado — tente novamente.';
}

export const queryClient = new QueryClient({
  // Rede de segurança: loga toda query/mutation que falhar, mesmo quando a
  // página não define seu próprio onError — sem isso, a maioria das mutações
  // do app falha em silêncio total (sem log, sem feedback ao usuário).
  queryCache: new QueryCache({
    onError: (error, query) => {
      logFrontendError('query', { queryKey: query.queryKey }, error);
    },
  }),
  mutationCache: new MutationCache({
    // Toda mutação que falha mostra toast — o usuário nunca fica sem saber
    // que a ação não foi salva, mesmo quando a página não define onError.
    onError: (error, _variables, _context, mutation) => {
      logFrontendError('mutation', { mutationKey: mutation.options.mutationKey }, error);
      toast.error(friendlyMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, err) => {
        if (err instanceof Error && err.name === 'ApiError') {
          const status = (err as unknown as { status?: number }).status;
          if (status && status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});
